<template>
  <div
    v-click-outside="close"
    class="relative page-select-menu"
  >
    <div
      :class="[inputClass, { 'cursor-pointer': !openMenu }]"
      class="
        relative
        flex
        items-center
        bg-white
        border border-[#E2E5EC]
        overflow-hidden
        h-[36px]
        min-w-[180px]
        page-select-box
      "
      @click.stop="open"
    >
      <div
        v-if="openMenu"
        class="w-[225px]"
        :class="inputSearchClass"
      >
        <PageSelectMenuSearch
          v-model="query"
          :placeholder="inputPlaceholder"
          @onSearch="onSearch"
        />
      </div>

      <div
        v-else
        class="w-full"
      >
        <div
          class="w-[225px] text-truncate overflow-hidden placeholder placeholder-pr-0"
          :class="inputSearchClass"
        >
          <slot name="placeholder">
            <PageSelectMenuSearch
              :placeholder="inputPlaceholder"
              :auto-focus="false"
            />
          </slot>
        </div>

        <span
          v-if="publishingIndicator"
          v-b-tooltip.hover
          :title="{'ACTIVE': 'Published', 'ACTIVE_LEGACY_CSS': 'Published (Legacy CSS)', 'DRAFT': 'Unpublished'}[currentPageStatus]"
          class="absolute right-8 transform top-1/2 -translate-y-1/2 w-2 h-2 rounded-full"
          :class="[{'ACTIVE': 'bg-[#86c158]', 'ACTIVE_LEGACY_CSS': 'bg-[#fb923c]', 'DRAFT': 'bg-[#E2E5EC]'}[currentPageStatus]]"
        />
        
        <svg
          class="absolute right-3 transform top-1/2 -translate-y-1/2"
          fill="none"
          height="5"
          viewBox="0 0 10 5"
          width="10"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            clip-rule="evenodd"
            d="M0.147926 0.146447C0.34516 -0.0488155 0.664941 -0.0488155 0.862175 0.146447L4.54545 3.79289L8.22873 0.146447C8.42597 -0.0488155 8.74575 -0.0488155 8.94298 0.146447C9.14022 0.341709 9.14022 0.658291 8.94298 0.853553L4.90258 4.85355C4.70534 5.04882 4.38556 5.04882 4.18833 4.85355L0.147926 0.853553C-0.0493086 0.658291 -0.0493086 0.341709 0.147926 0.146447Z"
            fill="#6E747A"
            fill-rule="evenodd"
          />
        </svg>
      </div>
    </div>

    <div
      v-if="openMenu"
      class="
        pages-select-menu-dropdwon
        absolute
        w-full
        transition-all
        z-builder-popover
        duration-[1s]
        top-0
        left-0
        rounded
        bg-white
        shadow
        translate-y-[50px]
        overflow-hidden
      "
      :class="menuClass"
    >
      <div v-if="getSelectedCategory">
        <button
          class="
            px-[12px]
            py-[10px]
            page-select-menu-back-button
            flex
            items-center
            w-full
            font-normal
            transition
            hover:bg-gray-200/80
            bg-[#F9F9F9]
          "
          @click="back"
        >
          <svg
            class="mr-2"
            fill="none"
            height="7"
            viewBox="0 0 13 7"
            width="13"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clip-rule="evenodd"
              d="M3.91248 0.170854C4.14028 0.39866 4.14028 0.768006 3.91248 0.995812L1.99162 2.91667H12.25C12.5722 2.91667 12.8333 3.17783 12.8333 3.5C12.8333 3.82217 12.5722 4.08333 12.25 4.08333H1.99162L3.91248 6.00419C4.14028 6.23199 4.14028 6.60134 3.91248 6.82915C3.68467 7.05695 3.31533 7.05695 3.08752 6.82915L0.170854 3.91248C-0.0569515 3.68467 -0.0569515 3.31533 0.170854 3.08752L3.08752 0.170854C3.31533 -0.0569515 3.68467 -0.0569515 3.91248 0.170854Z"
              fill="#505255"
              fill-rule="evenodd"
            />
          </svg>
          Go Back
        </button>
      </div>
      <div
        v-if="animated"
        class="
          absolute
          z-builder-popover
          bg-white
          mt-[25px]
          w-full
          text-center
          top-1/2
          left-1/2
          -translate-y-1/2 -translate-x-1/2
        "
      >
        <b-spinner
          small
          variant="primary"
        />
      </div>

      <div class="flex w-full font-bold border-bottom">
        <template v-if="!getSelectedCategory">
          <div class="flex w-full items-center">
            <button
              :class="{ 'text-[#AEB1B4]': tab }"
              class="
                flex
                text-[#505255] text-[14px]
                justify-content-between
                py-[12px]
                px-[12px]
                border-right
                w-full
              "
              @click.prevent="() => (tab = null)"
            >
              {{ title }}
              <button
                v-if="canCreate"
                v-b-tooltip.hover
                :disabled="tab"
                class="ml-auto cursor-pointer p-0"
                title="Create new folder"
                @click="createFolder"
              >
                <svg
                  fill="none"
                  height="15"
                  viewBox="0 0 18 15"
                  width="18"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_184_5)">
                    <path
                      :fill="!tab ? '#4F83E3' : '#AEB1B4'"
                      clip-rule="evenodd"
                      d="M17.2814 4.40492C17.1903 4.30198 17.0601 4.24316 16.9234 4.24316H15.0484V2.55146C15.0484 2.28314 14.8332 2.06562 14.5678 2.06562H6.31973V1.48584C6.31973 1.21753 6.10456 1 5.83915 1H1.48059C1.21517 1 1 1.21753 1 1.48584L1.00058 14.181C0.999712 14.293 1.03691 14.4029 1.10813 14.4912C1.19941 14.6043 1.33615 14.67 1.48059 14.67H15.8887C16.1336 14.67 16.3394 14.4837 16.3664 14.2376L17.4011 4.78238C17.4161 4.64512 17.3726 4.50785 17.2814 4.40492ZM1.96118 1.97169H5.35857V2.55146C5.35857 2.81977 5.57375 3.0373 5.83916 3.0373H14.0872V4.24316H3.40307C3.17495 4.24316 2.97826 4.40527 2.93235 4.63119L1.96118 9.40758V1.97169ZM2.06998 13.6984H15.4583L16.3867 5.21488H3.7949L2.06998 13.6984Z"
                      fill-rule="evenodd"
                    />
                    <path
                      :fill="!tab ? '#4F83E3' : '#AEB1B4'"
                      d="M17.281 4.40502L17.3933 4.30557V4.30555L17.281 4.40502ZM15.048 4.24326H14.898V4.39326H15.048V4.24326ZM6.31934 2.06572H6.16934V2.21572H6.31934V2.06572ZM0.999609 1.48594H0.849609V1.48595L0.999609 1.48594ZM1.00019 14.1811L1.15019 14.1823V14.1811H1.00019ZM1.10774 14.4913L1.2245 14.3971H1.22448L1.10774 14.4913ZM16.366 14.2377L16.5151 14.254L16.366 14.2377ZM17.4007 4.78248L17.2516 4.76616V4.76617L17.4007 4.78248ZM5.35818 1.97179H5.50818V1.82179H5.35818V1.97179ZM1.96079 1.97179V1.82179H1.81079V1.97179H1.96079ZM14.0868 3.0374H14.2368V2.8874H14.0868V3.0374ZM14.0868 4.24326V4.39326H14.2368V4.24326H14.0868ZM2.93196 4.63129L3.07895 4.66118V4.66116L2.93196 4.63129ZM1.96079 9.40768H1.81079L2.10778 9.43756L1.96079 9.40768ZM15.4579 13.6985V13.8485H15.5924L15.607 13.7148L15.4579 13.6985ZM2.06959 13.6985L1.92259 13.6686L1.88602 13.8485H2.06959V13.6985ZM16.3863 5.21498L16.5354 5.2313L16.5536 5.06498H16.3863V5.21498ZM3.79451 5.21498V5.06498H3.67194L3.64751 5.18509L3.79451 5.21498ZM16.923 4.39326C17.0164 4.39326 17.1058 4.43345 17.1688 4.50448L17.3933 4.30555C17.2739 4.17071 17.1029 4.09326 16.923 4.09326V4.39326ZM15.048 4.39326H16.923V4.09326H15.048V4.39326ZM14.898 2.55156V4.24326H15.198V2.55156H14.898ZM14.5674 2.21572C14.7484 2.21572 14.898 2.36454 14.898 2.55156H15.198C15.198 2.20194 14.9172 1.91572 14.5674 1.91572V2.21572ZM6.31934 2.21572H14.5674V1.91572H6.31934V2.21572ZM6.16934 1.48594V2.06572H6.46934V1.48594H6.16934ZM5.83876 1.1501C6.01979 1.1501 6.16934 1.29893 6.16934 1.48594H6.46934C6.46934 1.13633 6.18855 0.850098 5.83876 0.850098V1.1501ZM1.4802 1.1501H5.83876V0.850098H1.4802V1.1501ZM1.14961 1.48594C1.14961 1.29893 1.29916 1.1501 1.4802 1.1501V0.850098C1.13041 0.850098 0.849609 1.13633 0.849609 1.48594H1.14961ZM1.15019 14.1811L1.14961 1.48594L0.849609 1.48595L0.850186 14.1811H1.15019ZM1.22448 14.3971C1.17553 14.3364 1.14958 14.2604 1.15018 14.1823L0.85019 14.18C0.849064 14.3258 0.897509 14.4696 0.991007 14.5855L1.22448 14.3971ZM1.4802 14.5201C1.38147 14.5201 1.28752 14.4752 1.2245 14.3971L0.990986 14.5854C1.11052 14.7336 1.29005 14.8201 1.4802 14.8201V14.5201ZM15.8883 14.5201H1.4802V14.8201H15.8883V14.5201ZM16.2168 14.2213C16.1981 14.3927 16.0554 14.5201 15.8883 14.5201V14.8201C16.2111 14.8201 16.4799 14.5749 16.5151 14.254L16.2168 14.2213ZM17.2516 4.76617L16.2168 14.2213L16.5151 14.254L17.5498 4.7988L17.2516 4.76617ZM17.1687 4.50446C17.2317 4.57557 17.262 4.67075 17.2516 4.76616L17.5498 4.79881C17.5694 4.61969 17.5127 4.44033 17.3933 4.30557L17.1687 4.50446ZM5.35818 1.82179H1.96079V2.12179H5.35818V1.82179ZM5.50818 2.55156V1.97179H5.20818V2.55156H5.50818ZM5.83877 2.8874C5.65773 2.8874 5.50818 2.73857 5.50818 2.55156H5.20818C5.20818 2.90117 5.48898 3.1874 5.83877 3.1874V2.8874ZM14.0868 2.8874H5.83877V3.1874H14.0868V2.8874ZM14.2368 4.24326V3.0374H13.9368V4.24326H14.2368ZM3.40268 4.39326H14.0868V4.09326H3.40268V4.39326ZM3.07895 4.66116C3.11088 4.50407 3.24698 4.39326 3.40268 4.39326V4.09326C3.10214 4.09326 2.84486 4.30667 2.78496 4.60142L3.07895 4.66116ZM2.10778 9.43756L3.07895 4.66118L2.78497 4.6014L1.8138 9.37779L2.10778 9.43756ZM1.81079 1.97179V9.40768H2.11079V1.97179H1.81079ZM15.4579 13.5485H2.06959V13.8485H15.4579V13.5485ZM16.2372 5.19866L15.3088 13.6822L15.607 13.7148L16.5354 5.2313L16.2372 5.19866ZM3.79451 5.36498H16.3863V5.06498H3.79451V5.36498ZM2.21658 13.7284L3.9415 5.24487L3.64751 5.18509L1.92259 13.6686L2.21658 13.7284Z"
                    />
                    <rect
                      fill="white"
                      height="9"
                      width="8"
                      x="10"
                      y="6"
                    />
                    <path
                      :stroke="!tab ? '#4F83E3' : '#AEB1B4'"
                      d="M14 11L18 11M14 7L14 11L14 7ZM14 11L14 15L14 11ZM14 11L10 11L14 11Z"
                      stroke-linecap="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_184_5">
                      <rect
                        fill="white"
                        height="15"
                        width="18"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </button>

            <button
              v-if="!modulesLoading && getOthers.modules.length"
              :class="{ 'text-[#AEB1B4]': !tab }"
              class="
                flex
                text-[#505255] text-[14px]
                justify-content-between
                py-[12px]
                px-[12px]
                w-full
              "
              @click.prevent="() => (tab = 'modules')"
            >
              Modules

              <svg
                fill="none"
                height="18"
                viewBox="0 0 18 18"
                width="18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_4301_25790)">
                  <g clip-path="url(#clip1_4301_25790)">
                    <path
                      d="M20.237 0.462756C20.3585 0.620006 20.541 0.702639 20.7254 0.702639C20.8574 0.702639 20.99 0.660705 21.1022 0.573755C21.3717 0.365938 21.4216 -0.0213282 21.2132 -0.290812C20.3733 -1.37861 19.3947 -2.35665 18.3056 -3.19778C18.0361 -3.4056 17.6495 -3.35626 17.4404 -3.08678C17.2326 -2.81668 17.282 -2.43003 17.5514 -2.22221C18.5578 -1.44521 19.4613 -0.541795 20.237 0.462756Z"
                      fill="#AEB1B4"
                    />
                    <path
                      :fill="tab === 'modules' ? '#3D82EA' : '#AEB1B4'"
                      d="M7.13964 3.95001L3.58517 2.17278L0.0307007 3.95001L3.58517 5.72724L7.13964 3.95001Z"
                    />
                    <path
                      :fill="tab === 'modules' ? '#3D82EA' : '#AEB1B4'"
                      d="M12.8352 12.3477L9.13516 14.1977V17.7522L12.8352 15.9022V12.3477Z"
                    />
                    <path
                      :fill="tab === 'modules' ? '#3D82EA' : '#AEB1B4'"
                      d="M9.89737 3.95001L13.4518 5.72724L17.0063 3.95001L13.4518 2.17278L9.89737 3.95001Z"
                    />
                    <path
                      :fill="tab === 'modules' ? '#3D82EA' : '#AEB1B4'"
                      d="M14.0685 10.3522L17.7685 8.50223V4.94776L14.0685 6.79776V10.3522Z"
                    />
                    <path
                      :fill="tab === 'modules' ? '#3D82EA' : '#AEB1B4'"
                      d="M7.90184 4.94776L4.20184 6.79776V10.3522L7.90184 8.50223V4.94776Z"
                    />
                    <path
                      :fill="tab === 'modules' ? '#3D82EA' : '#AEB1B4'"
                      d="M12.8352 6.79776L9.13516 4.94776V8.50223L12.8352 10.3522V6.79776Z"
                    />
                    <path
                      :fill="tab === 'modules' ? '#3D82EA' : '#AEB1B4'"
                      d="M12.0724 11.35L8.51852 9.57276L4.96405 11.35L8.51852 13.1272L12.0724 11.35Z"
                    />
                    <path
                      :fill="tab === 'modules' ? '#3D82EA' : '#AEB1B4'"
                      d="M4.20184 15.9022L7.90184 17.7522V14.1977L4.20184 12.3477V15.9022Z"
                    />
                    <path
                      :fill="tab === 'modules' ? '#3D82EA' : '#AEB1B4'"
                      d="M-0.731506 8.50223L2.96849 10.3522V6.79776L-0.731506 4.94776V8.50223Z"
                    />
                  </g>
                </g>
                <defs>
                  <clipPath id="clip0_4301_25790">
                    <rect
                      fill="white"
                      height="18"
                      width="18"
                    />
                  </clipPath>
                  <clipPath id="clip1_4301_25790">
                    <rect
                      fill="white"
                      height="37"
                      transform="translate(-10 -9)"
                      width="37"
                    />
                  </clipPath>
                </defs>
              </svg>
            </button>
          </div>
        </template>

        <template v-else>
          <div class="flex align-items-center w-full py-[15px] px-[12px]">
            <span
              class="
                flex
                align-items-center
                text-[#44474A] text-[14px]
                font-normal
                mr-auto
              "
            >
              <svg
                class="mr-[12px]"
                fill="none"
                height="15"
                viewBox="0 0 18 15"
                width="18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clip-rule="evenodd"
                  d="M17.2814 4.40492C17.1903 4.30198 17.0601 4.24316 16.9234 4.24316H15.0484V2.55146C15.0484 2.28314 14.8332 2.06562 14.5678 2.06562H6.31973V1.48584C6.31973 1.21753 6.10456 1 5.83915 1H1.48059C1.21517 1 1 1.21753 1 1.48584L1.00058 14.181C0.999712 14.293 1.03691 14.4029 1.10813 14.4912C1.19941 14.6043 1.33615 14.67 1.48059 14.67H15.8887C16.1336 14.67 16.3394 14.4837 16.3664 14.2376L17.4011 4.78238C17.4161 4.64512 17.3726 4.50785 17.2814 4.40492ZM1.96118 1.97169H5.35857V2.55146C5.35857 2.81977 5.57375 3.0373 5.83916 3.0373H14.0872V4.24316H3.40307C3.17495 4.24316 2.97826 4.40527 2.93235 4.63119L1.96118 9.40758V1.97169ZM2.06998 13.6984H15.4583L16.3867 5.21488H3.7949L2.06998 13.6984Z"
                  fill="#4F83E3"
                  fill-rule="evenodd"
                />
                <path
                  d="M17.2814 4.40492L17.3937 4.30547L17.3937 4.30545L17.2814 4.40492ZM15.0484 4.24316H14.8984V4.39316H15.0484V4.24316ZM6.31973 2.06562H6.16973V2.21562H6.31973V2.06562ZM1 1.48584H0.85V1.48585L1 1.48584ZM1.00058 14.181L1.15058 14.1822L1.15058 14.181L1.00058 14.181ZM1.10813 14.4912L1.22489 14.397L1.22487 14.397L1.10813 14.4912ZM16.3664 14.2376L16.5155 14.2539V14.2539L16.3664 14.2376ZM17.4011 4.78238L17.252 4.76606V4.76607L17.4011 4.78238ZM5.35857 1.97169H5.50857V1.82169H5.35857V1.97169ZM1.96118 1.97169V1.82169H1.81118V1.97169H1.96118ZM14.0872 3.0373H14.2372V2.8873H14.0872V3.0373ZM14.0872 4.24316V4.39316H14.2372V4.24316H14.0872ZM2.93235 4.63119L3.07934 4.66108L3.07934 4.66106L2.93235 4.63119ZM1.96118 9.40758H1.81118L2.10817 9.43746L1.96118 9.40758ZM15.4583 13.6984V13.8484H15.5928L15.6074 13.7147L15.4583 13.6984ZM2.06998 13.6984L1.92298 13.6685L1.88641 13.8484H2.06998V13.6984ZM16.3867 5.21488L16.5358 5.2312L16.554 5.06488H16.3867V5.21488ZM3.7949 5.21488V5.06488H3.67233L3.6479 5.18499L3.7949 5.21488ZM16.9234 4.39316C17.0168 4.39316 17.1062 4.43335 17.1692 4.50438L17.3937 4.30545C17.2743 4.17061 17.1033 4.09316 16.9234 4.09316V4.39316ZM15.0484 4.39316H16.9234V4.09316H15.0484V4.39316ZM14.8984 2.55146V4.24316H15.1984V2.55146H14.8984ZM14.5678 2.21562C14.7488 2.21562 14.8984 2.36444 14.8984 2.55146H15.1984C15.1984 2.20184 14.9176 1.91562 14.5678 1.91562V2.21562ZM6.31973 2.21562H14.5678V1.91562H6.31973V2.21562ZM6.16973 1.48584V2.06562H6.46973V1.48584H6.16973ZM5.83915 1.15C6.02018 1.15 6.16973 1.29883 6.16973 1.48584H6.46973C6.46973 1.13623 6.18894 0.85 5.83915 0.85V1.15ZM1.48059 1.15H5.83915V0.85H1.48059V1.15ZM1.15 1.48584C1.15 1.29883 1.29955 1.15 1.48059 1.15V0.85C1.1308 0.85 0.85 1.13623 0.85 1.48584H1.15ZM1.15058 14.181L1.15 1.48584L0.85 1.48585L0.850577 14.181L1.15058 14.181ZM1.22487 14.397C1.17592 14.3363 1.14997 14.2603 1.15057 14.1822L0.850581 14.1799C0.849455 14.3257 0.8979 14.4695 0.991398 14.5854L1.22487 14.397ZM1.48059 14.52C1.38186 14.52 1.28791 14.4751 1.22489 14.397L0.991377 14.5853C1.11091 14.7335 1.29044 14.82 1.48059 14.82V14.52ZM15.8887 14.52H1.48059V14.82H15.8887V14.52ZM16.2172 14.2212C16.1985 14.3926 16.0558 14.52 15.8887 14.52V14.82C16.2115 14.82 16.4803 14.5748 16.5155 14.2539L16.2172 14.2212ZM17.252 4.76607L16.2172 14.2212L16.5155 14.2539L17.5502 4.7987L17.252 4.76607ZM17.1691 4.50436C17.2321 4.57547 17.2624 4.67065 17.252 4.76606L17.5502 4.79871C17.5698 4.61959 17.5131 4.44023 17.3937 4.30547L17.1691 4.50436ZM5.35857 1.82169H1.96118V2.12169H5.35857V1.82169ZM5.50857 2.55146V1.97169H5.20857V2.55146H5.50857ZM5.83916 2.8873C5.65812 2.8873 5.50857 2.73847 5.50857 2.55146H5.20857C5.20857 2.90107 5.48937 3.1873 5.83916 3.1873V2.8873ZM14.0872 2.8873H5.83916V3.1873H14.0872V2.8873ZM14.2372 4.24316V3.0373H13.9372V4.24316H14.2372ZM3.40307 4.39316H14.0872V4.09316H3.40307V4.39316ZM3.07934 4.66106C3.11127 4.50397 3.24737 4.39316 3.40307 4.39316V4.09316C3.10253 4.09316 2.84525 4.30657 2.78535 4.60132L3.07934 4.66106ZM2.10817 9.43746L3.07934 4.66108L2.78536 4.6013L1.81419 9.37769L2.10817 9.43746ZM1.81118 1.97169V9.40758H2.11118V1.97169H1.81118ZM15.4583 13.5484H2.06998V13.8484H15.4583V13.5484ZM16.2376 5.19856L15.3092 13.6821L15.6074 13.7147L16.5358 5.2312L16.2376 5.19856ZM3.7949 5.36488H16.3867V5.06488H3.7949V5.36488ZM2.21697 13.7283L3.94189 5.24477L3.6479 5.18499L1.92298 13.6685L2.21697 13.7283Z"
                  fill="#4F83E3"
                />
              </svg>

              {{ getSelectedCategory.name }}
            </span>

            <span
              v-if="!getSelectedCategory.hide_actions"
              v-b-tooltip.hover
              class="ml-auto mr-3 cursor-pointer"
              title="Rename folder"
              @click="editFolder"
            >
              <svg
                fill="none"
                height="15"
                viewBox="0 0 14 15"
                width="14"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clip-rule="evenodd"
                  d="M10.995 0.320023C10.7216 0.0466561 10.2784 0.0466561 10.005 0.320023L0.205025 10.12C0.0737498 10.2513 0 10.4293 0 10.615V13.415C0 13.8016 0.313401 14.115 0.7 14.115H3.5C3.68565 14.115 3.8637 14.0412 3.99497 13.91L13.795 4.10997C14.0683 3.83661 14.0683 3.39339 13.795 3.12002L10.995 0.320023ZM11.2 4.72505L9.38995 2.915L10.5 1.80495L12.3101 3.615L11.2 4.72505ZM8.4 3.90495L10.2101 5.715L4.2 11.725L2.38995 9.915L8.4 3.90495ZM1.4 10.9049L3.21005 12.715H1.4V10.9049Z"
                  fill="#3D82EA"
                  fill-rule="evenodd"
                />
                <path
                  d="M7.7 12.715C7.3134 12.715 7 13.0284 7 13.415C7 13.8016 7.3134 14.115 7.7 14.115H13.3C13.6866 14.115 14 13.8016 14 13.415C14 13.0284 13.6866 12.715 13.3 12.715H7.7Z"
                  fill="#3D82EA"
                />
              </svg>
            </span>
            <span
              v-if="!getSelectedCategory.hide_actions"
              v-b-tooltip.hover
              class="cursor-pointer"
              title="Delete folder"
              @click="deleteFolder"
            >
              <svg
                fill="none"
                height="17"
                viewBox="0 0 15 17"
                width="15"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.1576 5.76246L8.94671 5.71659L8.68419 13.1204L9.89508 13.1661L10.1576 5.76246Z"
                  fill="#3D82EA"
                />
                <path
                  d="M7.81539 5.73952H6.60371V13.1433H7.81539V5.73952Z"
                  fill="#3D82EA"
                />
                <path
                  d="M5.73473 13.1202L5.4722 5.71637L4.26131 5.76227L4.52386 13.1661L5.73473 13.1202Z"
                  fill="#3D82EA"
                />
                <path
                  d="M0 2.65287V3.90421H1.26259L2.26423 15.6592C2.29169 15.9822 2.55364 16.23 2.86765 16.23H11.531C11.8451 16.23 12.1072 15.982 12.1345 15.659L13.1361 3.90421H14.4191V2.65287H0ZM10.9761 14.9787H3.42243L2.47892 3.90421H11.9198L10.9761 14.9787Z"
                  fill="#3D82EA"
                />
                <path
                  d="M9.49386 0H5.58172C5.03143 0 4.58374 0.507722 4.58374 1.13181V3.28228H5.78132V1.35817H9.29426V3.28228H10.4918V1.13181C10.4918 0.507722 10.0442 0 9.49386 0Z"
                  fill="#3D82EA"
                />
              </svg>
            </span>
          </div>
        </template>
      </div>
      <div
        v-if="!tab"
        v-scroll="onScroll"
        :class="{ 'translate-x-[-100%]': animated }"
        class="h-auto max-h-[300px] overflow-auto transition"
      >
        <div
          v-if="!getItems.length && !loading"
          class="text-center text-[14px] pt-3 pb-2"
        >
          No pages
        </div>
        <template v-else>
          <div
            v-if="allPages"
            class="py-[10px] px-[12px] cursor-pointer hover:bg-gray-100 transition"
            @click.prevent="() => selectPage([])"
          >
            All pages
          </div>
          <PageSelectMenuOtherItem
            v-for="item in getOthers.additional"
            :key="item.id"
            :item="item"
            @click.native="() => selectOther(item)"
          />
          <PageSelectMenuRenderItems
            :items="getItems"
            :publishing-indicator="publishingIndicator"
            @onSelectCategory="selectCategory"
            @onSelectPage="selectPage"
          />
        </template>

        <div
          v-if="loading"
          class="flex items-center justify-center my-5"
        >
          <b-spinner
            small
            variant="primary"
          />
        </div>
      </div>
      <div
        v-else-if="tab === 'modules' && getOthers.modules.length"
        :class="{ 'translate-x-[-100%]': animated }"
      >
        <PageSelectMenuOtherItem
          v-for="item in getOthers.modules"
          :key="item.id"
          :item="item"
          @click.native="() => selectOther(item)"
        />
      </div>
    </div>

    <page-select-menu-create-folder v-model="createFolderModal" />

    <page-select-menu-edit-folder
      v-model="editFolderModal"
      :category="getSelectedCategory"
      @updated="() => back()"
    />

    <page-select-menu-delete-folder
      v-model="deleteFolderModal"
      :category="getSelectedCategory"
      @deleted="() => close(false, false)"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'
import ClickOutside from '@/directives/click-outside'
import PageSelectMenuRenderItems from '@/components/editor/components/pagesSelectMenu/PageSelectMenuRenderItems'
import PageSelectMenuSearch from '@/components/editor/components/pagesSelectMenu/PageSelectMenuSearch'
import PageSelectMenuOtherItem from '@/components/editor/components/pagesSelectMenu/PageSelectMenuOtherItem'
import PageSelectMenuCreateFolder from '@/components/editor/components/pagesSelectMenu/PageSelectMenuCreateFolder'
import PageSelectMenuDeleteFolder from '@/components/editor/components/pagesSelectMenu/PageSelectMenuDeleteFolder'
import PageSelectMenuEditFolder from '@/components/editor/components/pagesSelectMenu/PageSelectMenuEditFolder'

export default {
  name: 'PagesSelectMenu',
  components: {
    PageSelectMenuEditFolder,
    PageSelectMenuDeleteFolder,
    PageSelectMenuCreateFolder,
    PageSelectMenuOtherItem,
    PageSelectMenuSearch,
    PageSelectMenuRenderItems
  },
  directives: { ClickOutside },
  props: {
    inputClass: {
      type: String
    },
    inputSearchClass: {
      type: String,
      default:''
    },
    otherTitle: {
      type: String,
      default: 'Other:'
    },
    others: {
      type: Array,
      default: () => []
    },
    menuClass: {
      type: String,
      default: ''
    },
    title: { 
      type: String,
      default: 'Pages'
    },
    inputPlaceholder: { 
      type: String,
      default: 'Type to search pages'
    },
    addPosts: { 
      type: Boolean,
      default: false
    },
    allPages: {
      type: Boolean,
      default: false
    },
    publishingIndicator: {
      type: Boolean,
      default: false
    },
    canCreate: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      modulesLoading: false,
      filter: [],
      value: [],
      items: [],
      animated: false,
      loading: false,
      page: 1,
      category_id: null,
      query: '',
      pagination: {},
      openMenu: false,
      createFolderModal: false,
      deleteFolderModal: false,
      editFolderModal: false,
      tab: null,
      timer: null
    }
  },
  computed: {
    getOthers () {
      return {
        modules: this.others.filter((module) => {
          return (
            module.item_type === 'module' && !this.filter.includes(module.code)
          )
        }),
        additional: this.others.filter((module) => !module.item_type)
      }
    },
    currentPage () {
      return this.$store.state.pages.page
    },
    currentPageStatus () {
      if (this.currentPage.page_status === 'ACTIVE' && !this.currentPage.css_file) return 'ACTIVE_LEGACY_CSS'
      else return this.currentPage.page_status
    },
    ...mapState('projects', {
      project: (state) => state.project
    }),
    getSelectedCategory () {
      return _.last(this.value)
    },
    getItems () {
      const data  = _.cloneDeep(this.items)
      if(this.addPosts && !this.query && !this.getSelectedCategory){
        data.unshift({
          id:'posts',
          name:'Posts',
          hide_actions:true
        })
      }
      return data
    }
  },
  mounted () {
    VEvent.listen('open-page', (page) => {
      this.selectPage(page)
    })
  },
  methods: {
    deleteFolder () {
      this.deleteFolderModal = true
    },
    createFolder () {
      this.close()
      this.createFolderModal = true
    },
    editFolder () {
      this.editFolderModal = true
    },
    open () {
      if (this.openMenu) return

      this.loading = true
      this.fetchPages(this.currentPage.category || false)
      this.fetchModules()
      this.openMenu = true

      if (this.currentPage.category) {
        this.value = [this.currentPage.category]
      }
    },
    close (e, prevent = false) {
      if (
        prevent &&
        (document.querySelector('.delete-pages-folder-modal') ||
          document.querySelector('.update-folder-modal'))
      )
        return
      if (
        e &&
        (e.target.closest('.page-select-menu-back-button') ||
          e.target.closest('.delete-pages-folder-modal') ||
          e.target.closest('.update-folder-modal'))
      )
        return
      this.openMenu = false
      this.value = []
      this.pagination = {}
      this.page = 1
      this.query = ''
      clearTimeout(this.timer)
    },
    onScroll (evt) {
      if (this.animated || this.page === this.pagination.last_page) return

      if (
        Math.round(evt.target.scrollTop + evt.target.offsetHeight) >=
        Math.round(evt.target.scrollHeight)
      ) {
        this.page++
        this.loading = true
        this.fetchPages(this.getSelectedCategory || null)
      }
    },
    fetchModules () {
      this.modulesLoading = true
      axios
        .get(`api/modules?project_id=${this.project.id}`)
        .then(({ data }) => {
          data.data.forEach((module) => {
            if (module.id === 2) module.is_coming_soon = 0
            if (!module.user_status || !module.user_status.is_visible) {
              this.filter.push(module.code)
            }
          })
        })
        .finally(() => {
          this.modulesLoading = false
        })
    },
    fetchPages (category = null) {
      const params = {
        page: this.page,
        q: this.query,
        parentId: category ? category.id : null
      }
      const isPostsFolder = category && category.id === 'posts'
      if(isPostsFolder) params.parentId = null

      if(this.addPosts && (isPostsFolder || params.q)) params.show_posts =  1

      this.tab = null
      return axios
        .get(`api/projects/${this.project.id}/pages/with-categories`,{params})
        .then(({ data }) => {
          this.page = data.data.current_page
          this.pagination = {
            current_page: data.data.current_page,
            per_page: data.data.per_page,
            last_page: data.data.last_page,
            total: data.data.total
          }
          const posts = (data.data.posts||[]).map(p=> {
            return {
              id:p.id,
              title:p.name,
              slug:p.blog_slug + '/' + p.slug,
              category:{
                name:'Posts'
              }
            }
          })

          if (this.page === 1) {
           
            this.items = [
              ...(!this.query && !isPostsFolder ? data.data.categories : []),
              ...(this.query && this.addPosts ? posts : []),
              ...(isPostsFolder && !this.query ? posts :  data.data.data)
            ]
          } else {
            this.items = [...this.items, ...data.data.data]
          }

          if (category) {
            this.value = _.uniq([...this.value, category])
          }

          this.animated = false
          this.loading = false
        })
    },
    onSearch (query) {
      this.pagination = {}
      this.value = []
      this.query = query
      this.page = 1
      this.animated = true
      this.fetchPages()
    },
    back () {
      this.value.splice(-1, 1)
      this.defaultLoading(this.getSelectedCategory || null)
    },
    defaultLoading (category = null) {
      this.pagination = {}
      this.page = 1
      this.query = ''
      this.animated = true
      this.loading = true
      this.fetchPages(category)
    },
    selectCategory (category) {
      this.defaultLoading(category)
    },
    selectPage (page) {
      this.$emit('selectPage', page)
      this.close()
    },
    selectOther (item) {
      if (item.id === 'marketing-funnels') {
        window.location.href = `https://marketing-funnel-module.vercel.app/?project=${this.project.id}`
      }
      this.$emit('selectOther', item)
      this.close()
    }
  }
}
</script>
